import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import { pushLocation } from "../../routing/location-store";

interface RouterLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  /** Real destination — used as the anchor `href` so middle-click,
   *  Cmd/Ctrl-click, open-in-new-tab, and copy-link all behave normally. A
   *  same-page fragment (`#section`) is left to the browser to scroll. */
  href: string;
  /** Fires after a plain-click SPA navigation has been committed — for the
   *  drawer close on a mobile nav tap and similar post-navigation side effects.
   *  Not called for modified clicks, external hrefs (the browser owns those),
   *  or navigations an unsaved-work guard intercepted and the user cancelled —
   *  so a menu can pass it unconditionally and it only runs when we actually
   *  navigated. A guarded navigation the user confirms fires it on commit. */
  onNavigated?(): void;
  children: ReactNode;
}

/**
 * Router-aware anchor. Renders a real `href` and intercepts only plain
 * left-clicks for client-side navigation, letting modified clicks
 * (middle / Cmd / Ctrl / Shift) fall through to native browser behavior.
 * Without this, a JS-only nav handler on an `href="#"` breaks open-in-new-tab
 * and copy-link — the bug this primitive exists to prevent.
 *
 * Navigation self-serves off the shared location store, so a link never has to
 * be threaded a `navigate` prop from its host.
 *
 * Only root-relative hrefs (`/...`) are routed: the SPA router owns exactly
 * that namespace. Fragments keep the browser's in-page scroll, and external
 * schemes (`mailto:`, `https://...`) keep full native navigation — so mixed
 * menus can pass any href and each item does the right thing. Keeping that
 * distinction here means link consumers never have to know which hrefs the
 * router owns.
 */
export function RouterLink({ href, onNavigated, onClick, children, ...rest }: RouterLinkProps) {
  // A protocol-relative URL ("//host/path") also starts with "/" but is an
  // external destination — leave it to the browser like any absolute URL.
  const routed = href.startsWith("/") && !href.startsWith("//");

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (
      !routed ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    pushLocation(href, onNavigated);
  }

  return (
    // A non-routed link with no caller `onClick` stays a plain anchor with no JS
    // handler at all — a fragment scroll or external navigation the browser owns
    // outright, never a listener that only early-returns.
    <a href={href} onClick={onClick || routed ? handleClick : undefined} {...rest}>
      {children}
    </a>
  );
}
